import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { Canvas, useFrame } from '@react-three/fiber';
import { Environment, Lightformer } from '@react-three/drei';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

const dummy = new THREE.Object3D();
const quat = new THREE.Quaternion();
const axis = new THREE.Vector3();

/* 640+ instanced cubes forming an icosahedron shell that explodes & reconstructs on scroll */
const Core = ({ progressRef }) => {
  const inst = useRef();
  const mat = useRef();

  const { bases, dirs, seeds, count } = useMemo(() => {
    const raw = new THREE.IcosahedronGeometry(2.05, 3);
    const geo = mergeVertices(raw);
    raw.dispose();
    const pos = geo.attributes.position;
    const count = pos.count;
    const bases = pos.array.slice();
    geo.dispose();
    const dirs = new Float32Array(count * 3);
    const seeds = new Float32Array(count * 4);
    for (let i = 0; i < count; i++) {
      const x = bases[i * 3], y = bases[i * 3 + 1], z = bases[i * 3 + 2];
      const l = Math.hypot(x, y, z) || 1;
      dirs[i * 3] = x / l; dirs[i * 3 + 1] = y / l; dirs[i * 3 + 2] = z / l;
      seeds[i * 4] = 1 + Math.random() * 2.4;          // explode multiplier
      seeds[i * 4 + 1] = Math.random() * Math.PI * 2;  // rot axis theta
      seeds[i * 4 + 2] = Math.random() * Math.PI;      // rot axis phi
      seeds[i * 4 + 3] = 0.6 + Math.random() * 1.6;    // spin speed
    }
    return { bases, dirs, seeds, count };
  }, []);

  useFrame((state) => {
    if (!inst.current) return;
    const p = progressRef.current;
    const t = state.clock.elapsedTime;
    const explode = Math.sin(Math.min(p, 0.92) / 0.92 * Math.PI); // 0 → 1 → 0
    const glass = THREE.MathUtils.smoothstep(p, 0.55, 0.95);

    for (let i = 0; i < count; i++) {
      const off = explode * seeds[i * 4] * 3.2;
      dummy.position.set(
        bases[i * 3] + dirs[i * 3] * off,
        bases[i * 3 + 1] + dirs[i * 3 + 1] * off,
        bases[i * 3 + 2] + dirs[i * 3 + 2] * off
      );
      const th = seeds[i * 4 + 1], ph = seeds[i * 4 + 2];
      axis.set(Math.sin(ph) * Math.cos(th), Math.sin(ph) * Math.sin(th), Math.cos(ph));
      quat.setFromAxisAngle(axis, explode * seeds[i * 4 + 3] * 4 + t * 0.15);
      dummy.quaternion.copy(quat);
      dummy.scale.setScalar(0.1 * (1 + explode * 0.5));
      dummy.updateMatrix();
      inst.current.setMatrixAt(i, dummy.matrix);
    }
    inst.current.instanceMatrix.needsUpdate = true;
    inst.current.rotation.y = p * Math.PI * 1.2 + t * 0.05;

    if (mat.current) {
      mat.current.metalness = THREE.MathUtils.lerp(0.95, 0.1, glass);
      mat.current.roughness = THREE.MathUtils.lerp(0.2, 0.03, glass);
      mat.current.transmission = THREE.MathUtils.lerp(0.05, 0.85, glass);
      mat.current.color.setStyle(glass > 0.5 ? '#1a2f66' : '#1E2A4D');
    }
  });

  useEffect(() => {
    const m = inst.current;
    return () => { m?.geometry?.dispose(); };
  }, []);

  return (
    <instancedMesh ref={inst} args={[undefined, undefined, count]} frustumCulled={false}>
      <boxGeometry args={[1, 1, 1]} />
      <meshPhysicalMaterial
        ref={mat}
        color="#1E2A4D"
        metalness={0.95}
        roughness={0.2}
        clearcoat={1}
        transmission={0.05}
        envMapIntensity={1.5}
      />
    </instancedMesh>
  );
};

export const ExplodingCoreScene = ({ progressRef, dpr }) => (
  <Canvas
    dpr={dpr}
    camera={{ position: [0, 0.4, 8.5], fov: 45 }}
    gl={{ antialias: true, alpha: true, powerPreference: 'high-performance' }}
  >
    <fog attach="fog" args={['#FFFFFF', 10, 30]} />
    <ambientLight intensity={0.7} />
    <pointLight position={[6, 4, 5]} color="#E8B92E" intensity={90} distance={26} />
    <pointLight position={[-6, -3, 4]} color="#3B5BC7" intensity={80} distance={24} />
    <Core progressRef={progressRef} />
    <Environment resolution={64}>
      <Lightformer intensity={3} position={[0, 5, 6]} scale={[10, 4, 1]} color="#ffffff" />
      <Lightformer intensity={4} position={[7, -1, -3]} scale={[4, 9, 1]} color="#E8B92E" />
      <Lightformer intensity={3.5} position={[-7, 1, -3]} scale={[4, 9, 1]} color="#3B5BC7" />
    </Environment>
  </Canvas>
);
